/**
 * Epoch and bucket arithmetic for the chart.
 *
 * This is **time**, not money: JS `number` milliseconds and second-resolution
 * chart times. It lives in its own module so the money-adjacent modules can be
 * scanned by the no-float gate without dragging `Date` arithmetic into them
 * (`lib/bars.ts` takes a `bucketOf` callback and never touches a clock).
 */

/** Chart intervals, in minutes. `D1` is 1,440. */
export const INTERVALS = [
  { id: 'M1', label: 'M1', minutes: 1 },
  { id: 'M5', label: 'M5', minutes: 5 },
  { id: 'M15', label: 'M15', minutes: 15 },
  { id: 'H1', label: 'H1', minutes: 60 },
  { id: 'H4', label: 'H4', minutes: 240 },
  { id: 'D1', label: 'D1', minutes: 1_440 },
] as const;

export type IntervalId = (typeof INTERVALS)[number]['id'];

export function minutesFor(interval: IntervalId): number {
  const found = INTERVALS.find((entry) => entry.id === interval);
  return found === undefined ? 1 : found.minutes;
}

/** Floor a canonical ISO instant to the start of its `minutes` bucket (UTC). */
export function bucketOf(iso: string, minutes: number): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const width = minutes * 60_000;
  const floored = Math.floor(ms / width) * width;
  return new Date(floored).toISOString();
}

/** Chart time (seconds since epoch) from a canonical ISO instant. */
export function chartTime(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0;
}

/** ISO instant `days` before `now` (for a history fetch window). */
export function isoDaysBefore(nowMs: number, days: number): string {
  return new Date(nowMs - days * 86_400_000).toISOString();
}

export function isoMinutesBefore(nowMs: number, minutes: number): string {
  return new Date(nowMs - minutes * 60_000).toISOString();
}

/** Server-clock offset from the `server_time` in HELLO, sampled once per connect. */
export function offsetMs(serverIso: string, localMs: number): number {
  const server = Date.parse(serverIso);
  return Number.isFinite(server) ? server - localMs : 0;
}

/** The server clock, rendered as a wall-clock `HH:MM:SS`. */
export function serverClock(offset: number, localMs: number): string {
  return new Date(localMs + offset).toISOString().slice(11, 19);
}

/** The server date, for the "today" filter and the day-open reference. */
export function serverDate(offset: number, localMs: number): string {
  return new Date(localMs + offset).toISOString().slice(0, 10);
}

/** Start of a server day as a canonical ISO instant. */
export function startOfDayIso(date: string): string {
  return `${date}T00:00:00.000Z`;
}

/** How many M1 bars a chart of `candles` needs, capped by the history API limit. */
export function barsNeeded(candles: number, minutesPerCandle: number): number {
  return Math.min(5_000, candles * minutesPerCandle);
}

